import * as React from "react"
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu"
import { Check, ChevronRight, Circle } from "lucide-react"

import { cn } from "@/lib/utils"

const ContextMenuBase = ContextMenuPrimitive.Root

const ContextMenuTriggerBase = ContextMenuPrimitive.Trigger

const ContextMenuGroupBase = ContextMenuPrimitive.Group

const ContextMenuPortalBase = ContextMenuPrimitive.Portal

const ContextMenuSubBase = ContextMenuPrimitive.Sub

const ContextMenuRadioGroupBase = ContextMenuPrimitive.RadioGroup

const ContextMenuSubTriggerBase = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.SubTrigger> & {
    inset?: boolean
  }
>(({ className, inset, children, ...props }, ref) => (
  <ContextMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      "flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      inset && "pl-8",
      className
    )}
    {...props}
  >
    {children}
    <ChevronRight className="ml-auto" />
  </ContextMenuPrimitive.SubTrigger>
))
ContextMenuSubTriggerBase.displayName = ContextMenuPrimitive.SubTrigger.displayName

const ContextMenuSubContentBase = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <ContextMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    )}
    {...props}
  />
))
ContextMenuSubContentBase.displayName = ContextMenuPrimitive.SubContent.displayName

const ContextMenuContentBase = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Content>
>(({ className, ...props }, ref) => (
  <ContextMenuPrimitive.Portal>
    <ContextMenuPrimitive.Content
      ref={ref}
      className={cn(
        "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        className
      )}
      {...props}
    />
  </ContextMenuPrimitive.Portal>
))
ContextMenuContentBase.displayName = ContextMenuPrimitive.Content.displayName

const ContextMenuItemBase = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Item> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <ContextMenuPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0",
      inset && "pl-8",
      className
    )}
    {...props}
  />
))
ContextMenuItemBase.displayName = ContextMenuPrimitive.Item.displayName

const ContextMenuCheckboxItemBase = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <ContextMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <ContextMenuPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </ContextMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </ContextMenuPrimitive.CheckboxItem>
))
ContextMenuCheckboxItemBase.displayName = ContextMenuPrimitive.CheckboxItem.displayName

const ContextMenuRadioItemBase = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <ContextMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <ContextMenuPrimitive.ItemIndicator>
        <Circle className="h-2 w-2 fill-current" />
      </ContextMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </ContextMenuPrimitive.RadioItem>
))
ContextMenuRadioItemBase.displayName = ContextMenuPrimitive.RadioItem.displayName

const ContextMenuLabelBase = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Label> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <ContextMenuPrimitive.Label
    ref={ref}
    className={cn(
      "px-2 py-1.5 text-sm font-semibold",
      inset && "pl-8",
      className
    )}
    {...props}
  />
))
ContextMenuLabelBase.displayName = ContextMenuPrimitive.Label.displayName

const ContextMenuSeparatorBase = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <ContextMenuPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
))
ContextMenuSeparatorBase.displayName = ContextMenuPrimitive.Separator.displayName

const ContextMenuShortcutBase = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn("ml-auto text-xs tracking-widest opacity-60", className)}
      {...props}
    />
  )
}
ContextMenuShortcutBase.displayName = "ContextMenuShortcut"

// --- uiv2: conditional Intui swap (build-time resolved by Vite) ---
import {
  IntuiContextMenu,
  IntuiContextMenuTrigger,
  IntuiContextMenuContent,
  IntuiContextMenuItem,
  IntuiContextMenuLabel,
  IntuiContextMenuSeparator,
  IntuiContextMenuSub,
  IntuiContextMenuSubTrigger,
  IntuiContextMenuSubContent,
  IntuiContextMenuCheckboxItem,
  IntuiContextMenuRadioGroup,
  IntuiContextMenuRadioItem,
  IntuiContextMenuGroup,
  IntuiContextMenuShortcut,
} from '@/components/intueri/IntuiContextMenu'

const _uiv2 = import.meta.env.VITE_UIV2 === 'true'

// Lazy proxy: wraps Intui access in a render function to avoid TDZ errors from circular ESM imports.
function _lazy<T>(getIntui: () => unknown, base: T): T {
  if (!_uiv2) return base
  // Dynamic-dispatch proxy — `any` is required so the generic _lazy<T> can
  // forward refs/props to whichever Intui component getIntui() resolves at
  // render time. The outer `as unknown as T` cast re-narrows for callers.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return React.forwardRef<any, any>((props, ref) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Comp = getIntui() as any
    return <Comp ref={ref} {...props} />
  }) as unknown as T
}

const ContextMenu = _lazy(() => IntuiContextMenu, ContextMenuBase)
const ContextMenuTrigger = _lazy(() => IntuiContextMenuTrigger, ContextMenuTriggerBase)
const ContextMenuContent = _lazy(() => IntuiContextMenuContent, ContextMenuContentBase)
const ContextMenuItem = _lazy(() => IntuiContextMenuItem, ContextMenuItemBase)
const ContextMenuCheckboxItem = _lazy(() => IntuiContextMenuCheckboxItem, ContextMenuCheckboxItemBase)
const ContextMenuRadioItem = _lazy(() => IntuiContextMenuRadioItem, ContextMenuRadioItemBase)
const ContextMenuLabel = _lazy(() => IntuiContextMenuLabel, ContextMenuLabelBase)
const ContextMenuSeparator = _lazy(() => IntuiContextMenuSeparator, ContextMenuSeparatorBase)
const ContextMenuShortcut = _lazy(() => IntuiContextMenuShortcut, ContextMenuShortcutBase)
const ContextMenuGroup = _lazy(() => IntuiContextMenuGroup, ContextMenuGroupBase)
const ContextMenuSub = _lazy(() => IntuiContextMenuSub, ContextMenuSubBase)
const ContextMenuSubTrigger = _lazy(() => IntuiContextMenuSubTrigger, ContextMenuSubTriggerBase)
const ContextMenuSubContent = _lazy(() => IntuiContextMenuSubContent, ContextMenuSubContentBase)
const ContextMenuRadioGroup = _lazy(() => IntuiContextMenuRadioGroup, ContextMenuRadioGroupBase)
const ContextMenuPortal = ContextMenuPortalBase

export {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuCheckboxItem,
  ContextMenuRadioItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuGroup,
  ContextMenuPortal,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuRadioGroup,
  ContextMenuBase,
  ContextMenuTriggerBase,
  ContextMenuContentBase,
  ContextMenuItemBase,
  ContextMenuCheckboxItemBase,
  ContextMenuRadioItemBase,
  ContextMenuLabelBase,
  ContextMenuSeparatorBase,
  ContextMenuShortcutBase,
  ContextMenuGroupBase,
  ContextMenuPortalBase,
  ContextMenuSubBase,
  ContextMenuSubContentBase,
  ContextMenuSubTriggerBase,
  ContextMenuRadioGroupBase,
}
